/**
 *  @class
 *  @function CartDiscounts
 *
 *  CUSTOMISED. Vendor baseline: Fuel Themes "Vision" 10.0.0.
 *  This file is the source of truth and is intentionally unminified.
 *  `cart-discounts-backup.js` is the vendor's untouched copy — do not edit it;
 *  it exists so this file can be diffed against a future Fuel Themes update.
 *
 *  Note: a storefront app minifies theme JavaScript at request time and emits a
 *  source map, so the bytes the browser receives will not match this file.
 *  Verify changes against the served asset, not against this source.
 *
 *  The critical constraint in here: renderDiscounts() replaces the innerHTML of
 *  the container that holds this element, so `this` is DETACHED by the time the
 *  response is handled. querySelector still resolves inside the detached subtree,
 *  so writing state to `this` fails silently — no error, no visible effect. All
 *  post-render state must go through CartDiscounts.liveElements().
 */
if (!customElements.get('cart-discounts')) {
  class CartDiscounts extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {

      this.discounts();
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      }, {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    /**
     * Every <cart-discounts> currently in the document. Two are normally live at
     * once — one in the cart page section, one in the cart drawer — and a single
     * section swap replaces both.
     */
    static liveElements() {
      return Array.from(document.querySelectorAll('cart-discounts'));
    }
    static setLoading(root, isLoading) {
      const button = root.querySelector('.cart-discounts--button');
      if (button) {
        button.classList.toggle('loading', isLoading);
      }
    }
    static setError(root, isVisible) {
      const error = root.querySelector('.cart-discounts--error');
      if (!error) {
        return;
      }
      if (isVisible) {
        error.removeAttribute('hidden');
      } else {
        error.setAttribute('hidden', '');
      }
    }
    /**
     * The field lives inside a <details> that Liquid re-renders closed unless the
     * cart carries a cart-level discount. Left alone, the swap snaps the panel shut
     * and hides whatever outcome we just rendered into it.
     */
    static isPanelOpen(root) {
      const details = root.closest('details');
      return details ? details.open : true;
    }
    static setPanelOpen(root, isOpen) {
      const details = root.closest('details');
      if (details && isOpen) {
        details.open = true;
      }
    }
    static setInputValue(root, value) {
      const input = root.querySelector('.cart-discounts--input');
      if (input) {
        input.value = value;
      }
    }
    /**
     * Name the rejected code in the message. Falls back to the generic sentence the
     * snippet already rendered if the localised string is missing its placeholder,
     * so a lagging translation degrades rather than breaks.
     */
    static setErrorText(root, code) {
      const text = root.querySelector('.cart-discounts--error-text');
      const template = window.theme && theme.strings ? theme.strings.discountCodeErrorWithCode : null;
      if (!text || !code || !template || template.indexOf('%%CODE%%') === -1) {
        return;
      }
      text.textContent = template.replace('%%CODE%%', code);
    }

    discounts() {
      const button = this.querySelector('.cart-discounts--button');
      const remove_discount_buttons = this.querySelectorAll('.cart-discounts--remove')

      if (button) {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.updateDiscount();
        });
      }
      // Bound outside the button check: applied discounts are still removable when
      // the add-a-code form itself is disabled by theme settings.
      remove_discount_buttons.forEach((remove_discount_button) => {
        remove_discount_button.addEventListener('click', this.removeDiscount.bind(this));
      });
    }
    updateDiscount() {
      const input = this.querySelector('.cart-discounts--input');
      if (!input) {
        return;
      }
      const discountCodeValue = input.value.trim();
      if (!discountCodeValue) {
        return;
      }
      const existingDiscounts = this.existingDiscounts();

      if (existingDiscounts.some((code) => code.toLowerCase() === discountCodeValue.toLowerCase())) {
        return;
      }
      existingDiscounts.push(discountCodeValue);

      this.renderDiscounts(existingDiscounts, discountCodeValue);
    }
    removeDiscount(event) {
      const row = event.target.closest('.cart-discounts--row');
      const pill = row ? row.querySelector('.cart-discounts--name') : null;
      if (!pill) {
        return;
      }
      const discountCode = pill.textContent.trim();
      const existingDiscounts = this.existingDiscounts();
      const index = existingDiscounts.findIndex((code) => code.toLowerCase() === discountCode.toLowerCase());
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      this.renderDiscounts(existingDiscounts);
    }
    existingDiscounts() {
      const discountCodes = [];
      const discountPills = this.querySelectorAll('.cart-discounts--name');
      for (const pill of discountPills) {
        const code = pill.textContent.trim();
        if (code) {
          discountCodes.push(code);
        }
      }

      return discountCodes;
    }
    /**
     * Shopify keeps a refused code on the cart, where it silently rides into checkout
     * on the URL and surfaces there as an error the customer never asked for. Put the
     * cart back to the codes that actually applied. No sections are re-rendered: the
     * refused code has no allocation, so nothing on screen changes.
     */
    dropRejectedCode(submittedDiscounts, rejectedCode) {
      const acceptedDiscounts = submittedDiscounts.filter((code) => {
        return code.toLowerCase() !== rejectedCode.toLowerCase();
      });

      return fetch(`${theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        body: JSON.stringify({ discount: acceptedDiscounts.join(',') })
      }).catch(() => {
        // Not worth surfacing: the customer already has the rejection message.
      });
    }
    renderDiscounts(existingDiscounts, discountCodeValue) {
      const body = JSON.stringify({
        discount: existingDiscounts.join(','),
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });
      const wasPanelOpen = CartDiscounts.isPanelOpen(this);

      CartDiscounts.setLoading(this, true);
      CartDiscounts.setError(this, false);

      fetch(`${theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        ...{
          body
        }
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);

            if (parsedState.sections && elementToReplace) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }));

          // Shopify reports the verdict as a boolean on the cart's discount_codes
          // collection. It carries no reason; checkout has that, the cart does not.
          const wasRejected = Boolean(discountCodeValue) && (parsedState.discount_codes || []).some((discount) => {
            return discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false;
          });

          if (wasRejected) {
            this.dropRejectedCode(existingDiscounts, discountCodeValue);
          }

          // From here on `this` is detached — see the note at the top of this file.
          CartDiscounts.liveElements().forEach((element) => {
            CartDiscounts.setLoading(element, false);
            CartDiscounts.setError(element, wasRejected);
            CartDiscounts.setPanelOpen(element, wasPanelOpen || wasRejected);
            if (wasRejected) {
              CartDiscounts.setErrorText(element, discountCodeValue);
              // Put the rejected code back in the field, so the customer can see and
              // correct what they typed instead of watching it silently disappear.
              CartDiscounts.setInputValue(element, discountCodeValue);
            }
          });
        })
        .catch(() => {
          // A failed request or re-render must not leave the control spinning.
          CartDiscounts.liveElements().forEach((element) => {
            CartDiscounts.setLoading(element, false);
            CartDiscounts.setPanelOpen(element, wasPanelOpen);
          });
        });
    }
  }
  customElements.define('cart-discounts', CartDiscounts);
}
